import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaTachometerAlt,
  FaNewspaper,
  FaFolderOpen,
  FaUsers,
  FaCog,
  FaSignOutAlt,
  FaGlobe,
} from 'react-icons/fa';

const navigation = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: FaTachometerAlt },
  { label: 'Blog Management', path: '/admin/blogs', icon: FaNewspaper },
  { label: 'Categories', path: '/admin/categories', icon: FaFolderOpen },
  { label: 'User Management', path: '/admin/users', icon: FaUsers },
  { label: 'Settings', path: '/admin/settings', icon: FaCog },
];

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      return null;
    }
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
    if (!userInfo?.token || !userInfo?.isAdmin) {
      navigate('/login', { replace: true });
    } else {
      setCurrentUser(userInfo);
    }
  }, [navigate]);

  const pageTitle = useMemo(() => {
    const matched = navigation.find((item) => location.pathname.startsWith(item.path));
    return matched?.label || 'Admin Panel';
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const sidebarContent = (
    <div className="flex h-full flex-col bg-slate-950 text-slate-100 border-r border-slate-900 shadow-xl">
      {/* Brand Header */}
      <div className="border-b border-white/10 px-5 py-4 bg-slate-950/80">
        <NavLink
          to="/admin/dashboard"
          className="flex items-center justify-center rounded-xl bg-white px-3 py-2 shadow-sm transition hover:shadow-md hover:bg-slate-50 group"
          title="Finegal Tech Solution — Admin Dashboard"
        >
          <img
            src="/logo.png"
            alt="Finegal Tech Solution"
            className="h-8 w-auto max-h-8 object-contain transition-transform duration-200 group-hover:scale-[1.02]"
          />
        </NavLink>
      </div>

      {/* Navigation links */}
      <nav className="flex-1 space-y-1.5 px-3.5 py-5 overflow-y-auto">
        <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Main Menu
        </p>
        {navigation.map(({ label, path, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition ${
              isActive ? 'bg-amber-400 text-slate-950' : 'text-slate-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Icon className="text-base" />
            <span>{label}</span>
          </NavLink>
        ))}

        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-red-300 transition hover:bg-red-500/10 hover:text-red-200"
        >
          <FaSignOutAlt className="text-base" />
          <span>Logout</span>
        </button>
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="fixed left-4 top-4 z-40 rounded-lg bg-slate-950 px-3 py-2 text-white shadow-lg"
          aria-label="Open admin menu"
        >
          <FaBars className="text-lg" />
        </button>
      </div>

      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:w-72 lg:overflow-hidden">
        {sidebarContent}
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 lg:hidden">
          <div className="absolute left-0 top-0 h-full w-72 overflow-hidden shadow-2xl">
            <div className="absolute right-3 top-3">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-lg bg-white/10 p-2 text-white"
                aria-label="Close admin menu"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      <div className="lg:pl-72 flex min-h-screen flex-col">
        <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/95 backdrop-blur-md shadow-2xs">
          <div className="flex items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <div className="hidden sm:block h-8 w-1 rounded-full bg-amber-400" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">Admin</span>
                  <span className="text-xs text-slate-400 font-medium">Finegal Tech Workspace</span>
                </div>
                <h1 className="text-lg font-bold text-slate-900 sm:text-xl tracking-tight">{pageTitle}</h1>
              </div>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-3">
              <NavLink
                to="/"
                target="_blank"
                rel="noreferrer"
                className="hidden md:flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-slate-900"
                title="View live website in new tab"
              >
                <FaGlobe className="text-slate-400" />
                <span>Live Website</span>
              </NavLink>

              <div className="h-6 w-[1px] bg-slate-200 hidden md:block" />

              <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/80 px-2.5 py-1.5 shadow-2xs">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#01295c] text-xs font-bold text-white shadow-xs">
                  {(currentUser?.name || 'A').slice(0, 2).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left pr-1">
                  <p className="text-xs font-bold text-slate-900 leading-tight">{currentUser?.name || 'Admin'}</p>
                  <p className="text-[10px] font-medium text-slate-500 leading-tight truncate max-w-[130px]">{currentUser?.email || 'admin@example.com'}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
